import React,{useState,useEffect,useRef}from'react';
import api from'../../services/api';
import{calcAge}from'../../utils/helpers';

interface Props{
  label?:string;
  selected:any|null;
  onSelect:(p:any)=>void;
  onClear:()=>void;
  placeholder?:string;
}

export default function PatientSearchInput({
  label,selected,onSelect,onClear,
  placeholder='Search by name, UHID or phone...'
}:Props){
  const [q,setQ]=useState('');
  const [results,setResults]=useState<any[]>([]);
  const [recent,setRecent]=useState<any[]>([]);
  const [show,setShow]=useState(false);
  const [loading,setLoading]=useState(false);
  const [recentLoading,setRecentLoading]=useState(true);
  const ref=useRef<HTMLDivElement>(null);

  useEffect(()=>{
    api.getPatients({take:40,skip:0})
      .then((r:any)=>{
        const list=r?.data?.patients||r?.data?.data||r?.data||[];
        setRecent(Array.isArray(list)?list:[]);
      })
      .catch(()=>setRecent([]))
      .finally(()=>setRecentLoading(false));
  },[]);

  useEffect(()=>{
    const h=(e:MouseEvent)=>{if(ref.current&&!ref.current.contains(e.target as Node))setShow(false);};
    document.addEventListener('mousedown',h);
    return()=>document.removeEventListener('mousedown',h);
  },[]);

  useEffect(()=>{
    if(q.length<2){setResults([]);return;}
    const t=setTimeout(async()=>{
      setLoading(true);
      try{
        const r=await api.getPatients({search:q,take:20});
        const list=r?.data?.patients||r?.data?.data||r?.data||[];
        setResults(Array.isArray(list)?list:[]);
      }catch{setResults([]);}
      finally{setLoading(false);}
    },300);
    return()=>clearTimeout(t);
  },[q]);

  const pick=(p:any)=>{setQ('');setResults([]);setShow(false);onSelect(p);};
  const clear=()=>{setQ('');setResults([]);onClear();};
  const displayList=q.length>=2?results:recent;
  const isRecent=q.length<2;

  const Avatar=({name,size='sm'}:{name:string,size?:'sm'|'lg'})=>(
    <div className={`rounded-full bg-gradient-to-br from-[#015CA6] to-[#039089] flex items-center justify-center text-white font-bold flex-shrink-0 ${size==='lg'?'w-11 h-11 text-base':'w-9 h-9 text-sm'}`}>
      {name?.[0]?.toUpperCase()||'?'}
    </div>
  );

  const BloodBadge=({bg}:{bg:string})=>(
    <span className="inline-flex items-center gap-0.5 text-xs font-bold text-red-600 bg-red-50 border border-red-100 px-1.5 py-0.5 rounded-md">🩸{bg}</span>
  );

  const Row=({p}:{p:any})=>{
    const name=`${p.firstName||p.first_name||''} ${p.lastName||p.last_name||''}`.trim();
    const dob=p.dateOfBirth||p.date_of_birth;
    const bg=p.bloodGroup||p.blood_group;
    const phone=p.phone||p.mobile;
    return(
      <div onClick={()=>pick(p)}
        className="group flex items-center gap-3 px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors border-b border-slate-100 last:border-0">
        <Avatar name={name}/>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-semibold text-sm text-slate-800 group-hover:text-[#02357A] transition-colors">{name||'Unknown Patient'}</span>
            <span className="font-mono text-xs text-[#02357A] bg-[#02357A]/8 px-2 py-0.5 rounded-md border border-[#02357A]/20">{p.uhid||'—'}</span>
            {bg&&<BloodBadge bg={bg}/>}
          </div>
          <div className="flex items-center gap-3 mt-0.5 flex-wrap">
            {dob&&<span className="text-xs text-slate-500">{calcAge(dob)}</span>}
            {p.gender&&<span className="text-xs text-slate-400">• {p.gender}</span>}
            {phone&&<span className="text-xs text-slate-500">📱 {phone}</span>}
            {(p.abhaNumber||p.abha_number)&&<span className="text-xs text-emerald-600 font-medium">🔗 ABHA</span>}
          </div>
        </div>
        <span className="text-slate-300 group-hover:text-[#02357A] text-lg transition-colors flex-shrink-0">›</span>
      </div>
    );
  };

  /* ── Selected state ─────────────────────────────────── */
  if(selected){
    const name=`${selected.firstName||selected.first_name||''} ${selected.lastName||selected.last_name||''}`.trim();
    const dob=selected.dateOfBirth||selected.date_of_birth;
    const bg=selected.bloodGroup||selected.blood_group;
    const allergies=selected.knownAllergies||selected.known_allergies;
    const phone=selected.phone||selected.mobile;
    return(
      <div>
        {label&&<label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}
        <div className="flex items-center gap-3 p-3.5 rounded-2xl border-2 border-[#02357A]/30 bg-gradient-to-r from-blue-50 to-slate-50 shadow-sm">
          <Avatar name={name} size="lg"/>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <span className="font-bold text-slate-800 text-base">{name||'Unknown'}</span>
              <span className="font-mono text-xs text-[#02357A] bg-white px-2 py-0.5 rounded-lg border border-[#02357A]/20 shadow-sm">{selected.uhid}</span>
              {bg&&<BloodBadge bg={bg}/>}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {dob&&<span className="text-xs text-slate-500 font-medium">{calcAge(dob)}</span>}
              {selected.gender&&<span className="text-xs text-slate-400">• {selected.gender}</span>}
              {phone&&<span className="text-xs text-slate-500">📱 {phone}</span>}
            </div>
            {allergies&&(
              <div className="mt-1.5 flex items-center gap-1.5">
                <span className="text-xs font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-lg">⚠️ {allergies}</span>
              </div>
            )}
          </div>
          <button onClick={clear}
            className="flex-shrink-0 text-xs font-semibold text-slate-500 hover:text-red-500 bg-white hover:bg-red-50 border border-slate-200 hover:border-red-200 px-3 py-1.5 rounded-xl transition-all">
            Change
          </button>
        </div>
      </div>
    );
  }

  /* ── Search input ───────────────────────────────────── */
  return(
    <div ref={ref} className="relative">
      {label&&<label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}</label>}

      <div className="relative">
        <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-base pointer-events-none">🔍</span>
        <input
          value={q}
          onChange={e=>setQ(e.target.value)}
          onFocus={()=>setShow(true)}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2.5 border-2 border-slate-200 rounded-xl text-sm bg-white focus:outline-none focus:border-[#02357A] focus:ring-2 focus:ring-[#02357A]/10 transition-all placeholder:text-slate-400"
        />
        <div className="absolute right-3 top-1/2 -translate-y-1/2">
          {loading
            ?<div className="w-4 h-4 border-2 border-[#02357A]/30 border-t-[#02357A] rounded-full animate-spin"/>
            :q
              ?<button onClick={()=>{setQ('');setResults([]);}} className="text-slate-300 hover:text-slate-600 text-xl leading-none transition-colors">×</button>
              :<span className="text-slate-300 text-xs">↵</span>
          }
        </div>
      </div>

      {show&&(
        <div className="absolute z-50 top-full left-0 right-0 mt-1.5 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden"
          style={{maxHeight:'400px',overflowY:'auto'}}>

          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2.5 bg-slate-50 border-b border-slate-100 sticky top-0 z-10">
            <div className="flex items-center gap-2">
              <span className="text-base">{isRecent?'🕐':'🔍'}</span>
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                {isRecent?'Latest Patients':'Search Results'}
              </span>
            </div>
            <span className="text-xs text-slate-400 font-medium">
              {isRecent
                ?(recentLoading?'Loading…':`${recent.length} patients`)
                :(loading?'Searching…':`${results.length} found`)
              }
            </span>
          </div>

          {/* Skeleton */}
          {isRecent&&recentLoading&&(
            <div className="px-4 py-3 space-y-3">
              {[1,2,3,4].map(i=>(
                <div key={i} className="flex items-center gap-3 animate-pulse">
                  <div className="w-9 h-9 rounded-full bg-slate-200 flex-shrink-0"/>
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-slate-200 rounded-full w-2/5"/>
                    <div className="h-2.5 bg-slate-100 rounded-full w-1/3"/>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results */}
          {!recentLoading&&displayList.length>0&&displayList.map((p:any)=><Row key={p.id} p={p}/>)}

          {/* Empty states */}
          {!recentLoading&&isRecent&&recent.length===0&&(
            <div className="py-10 text-center">
              <span className="text-3xl block mb-2">👤</span>
              <p className="text-sm font-medium text-slate-500">No patients registered yet</p>
            </div>
          )}
          {!isRecent&&!loading&&results.length===0&&q.length>=2&&(
            <div className="py-10 text-center">
              <span className="text-3xl block mb-2">🔍</span>
              <p className="text-sm font-medium text-slate-500">No results for "<strong>{q}</strong>"</p>
              <p className="text-xs text-slate-400 mt-1">Try UHID, full name or phone number</p>
            </div>
          )}

          {/* Footer */}
          <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-100 sticky bottom-0 flex items-center justify-between">
            <a href="/patients/register"
              className="text-xs font-semibold text-[#02357A] hover:text-[#015CA6] hover:underline transition-colors flex items-center gap-1">
              <span className="text-base">➕</span> Register New Patient
            </a>
            {isRecent&&!recentLoading&&recent.length>0&&(
              <span className="text-xs text-slate-400">Most recent first</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
